import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import TabNav from './TabNav';
import { TABS, TabId, tabFromHash } from './tabConfig';
import Footer from '@/components/shared/Footer';

// Lazy-load each tab to avoid a massive initial bundle
const CommunityTab  = lazy(() => import('./Community/CommunityTab'));
const GravityTab    = lazy(() => import('./Gravity/GravityTab'));
const MinistriesTab = lazy(() => import('./Ministries/MinistriesTab'));
const ConnectTab    = lazy(() => import('./Connect/ConnectTab'));
const VolunteerTab  = lazy(() => import('./Volunteer/VolunteerTab'));
const LoginTab      = lazy(() => import('./Login/LoginTab'));

const TAB_COMPONENTS: Record<TabId, React.ComponentType> = {
  community:   CommunityTab,
  gravity:     GravityTab,
  ministries:  MinistriesTab,
  connect:     ConnectTab,
  volunteer:   VolunteerTab,
  login:       LoginTab,
};

/** Magazine-style clip-path page-turn config */
const getClipPaths = (direction: 'forward' | 'backward') => ({
  enterFrom: direction === 'forward' ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)',
  exitTo: direction === 'forward' ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)',
});

const TabShell: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const hash = window.location.hash;
    return tabFromHash(hash).id;
  });
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');

  // Sync to hash on back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const next = tabFromHash(window.location.hash);
      const currentIndex = TABS.findIndex((t) => t.id === activeTab);
      const nextIndex = next.index;
      setDirection(nextIndex >= currentIndex ? 'forward' : 'backward');
      setActiveTab(next.id);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeTab]);

  const handleTabChange = (tabId: TabId, dir: 'forward' | 'backward') => {
    setDirection(dir);
    setActiveTab(tabId);
  };

  const activeIndex = TABS.findIndex((t) => t.id === activeTab);
  const { enterFrom, exitTo } = getClipPaths(direction);

  const ActiveComponent = TAB_COMPONENTS[activeTab];

  return (
    <div style={{ background: 'var(--color-bg)' }}>
      <TabNav
        activeTab={activeTab}
        activeIndex={activeIndex}
        onTabChange={handleTabChange}
      />

      {/* Tab panel container — sits below the 64px header */}
      <div
        className="relative"
        style={{ marginTop: '64px', height: 'calc(100svh - 64px)', overflow: 'hidden' }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            className="tab-panel"
            style={{ height: '100%' }}
            initial={{ clipPath: enterFrom }}
            animate={{ clipPath: 'inset(0 0% 0 0)', transition: { duration: 0.75, ease: [0.76, 0, 0.24, 1] } }}
            exit={{ clipPath: exitTo, opacity: 0.6, transition: { duration: 0.55, ease: [0.76, 0, 0.24, 1] } }}
          >
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full">
                  <span className="eyebrow text-cr-white">Loading</span>
                </div>
              }
            >
              <ActiveComponent />
              {activeTab !== 'login' && <Footer />}
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TabShell;
